import { findHubLevel, FUNGI_ARENA_LEVEL_ID, MOON_ARENA_LEVEL_ID } from './hubLevels';
import { findOriginLevel } from './originEvent';
import { LevelDef, PlotDef } from '../core/game';
import { EnemyType } from '../core/types';
import { LEVELS } from './levels';
import { CREATOR_LEVEL_ID, EXPLORE_LEVEL_ID } from './creator';

/**
 * The Test Arena's level id.
 *
 * Deliberately far above every campaign level id AND deliberately NOT a member
 * of LEVELS. The campaign's unlock cursor is a single monotonic integer
 * (progressService.unlockedThrough), and LAST_LEVEL_ID / the level-select rows /
 * the pinned counts in tests/unit/levels.test.ts all derive from LEVELS. Keeping
 * the arena out of that array means the sandbox cannot perturb any of it.
 * Resolve levels through findLevel() rather than LEVELS.find() so both work.
 */
export const ARENA_LEVEL_ID = 900;

/** Sweep rows of the serpentine trail (y), and the plot bands between them. */
const ROW_Y = [130, 240, 350, 460, 570];
const BAND_Y = [185, 295, 405, 515, 640];

/**
 * The trail: five full-width sweeps stitched by short verticals, entering at the
 * pitch's left tunnel and exiting right. ~4.7k px long — far more trail than any
 * campaign level, which is the point: the arena exists to watch towers work.
 */
const ARENA_PATH = [
  { x: 120, y: ROW_Y[0] },
  { x: 980, y: ROW_Y[0] },
  { x: 980, y: ROW_Y[1] },
  { x: 140, y: ROW_Y[1] },
  { x: 140, y: ROW_Y[2] },
  { x: 980, y: ROW_Y[2] },
  { x: 980, y: ROW_Y[3] },
  { x: 140, y: ROW_Y[3] },
  { x: 140, y: ROW_Y[4] },
  { x: 1040, y: ROW_Y[4] },
];

/**
 * Plot bands sit midway between sweeps, so every plot covers two lanes of trail.
 * The x offsets alternate per band to dodge the stitching verticals (x=980 on
 * the odd bands, x=140 on the even ones) while holding ≥44px trail clearance and
 * ≥96px plot spacing.
 */
const BAND_X: number[][] = [
  [200, 320, 440, 560, 680, 800, 920],
  [240, 360, 480, 600, 720, 840, 940],
  [200, 320, 440, 560, 680, 800, 920],
  [240, 360, 480, 600, 720, 840, 940],
  [260, 380, 500, 620, 740, 860],
];

const ARENA_PLOTS: PlotDef[] = BAND_Y.flatMap((y, band) =>
  BAND_X[band].map((x, i) => ({ id: band * 10 + i, pos: { x, y } })),
);

/**
 * The Arena — a sandbox stadium. No waves of its own (they are built on demand
 * by the Start Wave dialog), no coin reward, no score. freeBuild makes towers
 * cost nothing; sandbox stops the run from persisting anything.
 */
export const TEST_ARENA: LevelDef = {
  id: ARENA_LEVEL_ID,
  name: 'The Arena',
  subtitle: 'Training Stadium',
  background: 'bg-arena',
  path: ARENA_PATH,
  plots: ARENA_PLOTS,
  startCash: 0,
  startLives: 20,
  coinReward: 0,
  waves: [],
  freeBuild: true,
  sandbox: true,
};

/**
 * The Moon Arena — The Hub's own sandbox (the red TEST button on The Moon's
 * chapter screen). The training stadium's trail and plots over a lava floor,
 * towers at the chapter's 10 HP, and ONLY The Moon's own enemies to pick
 * from. Its wave dialog carries the extra "Friendly" toggle.
 */
export { MOON_ARENA_LEVEL_ID, FUNGI_ARENA_LEVEL_ID };

export const MOON_ARENA: LevelDef = {
  ...TEST_ARENA,
  id: MOON_ARENA_LEVEL_ID,
  name: 'The Moon Arena',
  subtitle: 'Lava Pit',
  background: 'bg-moonarena',
  towerHp: 10,
};

/** The enemies The Moon Arena offers — The Moon's roster and nothing else. */
export const MOON_ARENA_ENEMY_TYPES: EnemyType[] = [
  'cosmiccorroder',
  'nuisancenugget',
  'barwaiter',
  'burntfry',
  'microwavednugget',
  'combo199',
  'chiply',
  'microwave',
  'chiplysdad',
  // Constellation Valley and Bun Beach (spec 2026-09-09).
  'nacho',
  'seasonezchippington',
  'lifeguard',
  // The nacho stages and the finale (spec 2026-10-08: they had never been
  // added). Prince Nacho is sent as his first form and transforms on the
  // spot, as he does on wave 30.
  'moonnacho',
  'spicednacho',
  'lasernacho',
  'princenacho',
];

/**
 * The Origin Event's two Test Arenas (spec 2026-10-08) — the red TEST button
 * on each chapter's menu, where the Moon's sits. The training stadium's trail
 * and plots, towers at 10 HP, and ONLY that chapter's enemies; the wave
 * dialog carries the Moon Arena's Friendly and Disable-boss toggles.
 */
export const SHADOW_ARENA_LEVEL_ID = 902;
export const CLOUD_ARENA_LEVEL_ID = 903;

/**
 * The Dark Shadows' arena: a dark forest whose lamp posts ring a wide empty
 * clearing, so the serpentine trail never crosses a tree.
 */
export const SHADOW_ARENA: LevelDef = {
  ...MOON_ARENA,
  id: SHADOW_ARENA_LEVEL_ID,
  name: 'The Shadow Arena',
  subtitle: 'The Dark Shadows',
  background: 'bg-shadowarena',
};

/** Above the Clouds' arena: the cloud floor, pink clouds mixed in. */
export const CLOUD_ARENA: LevelDef = {
  ...MOON_ARENA,
  id: CLOUD_ARENA_LEVEL_ID,
  name: 'The Cloud Arena',
  subtitle: 'Above the Clouds',
  background: 'bg-cloudarena',
};

/**
 * The Fungi Forest's arena (spec 2026-10-20): a mossy clearing ringed with
 * small mushrooms, the training stadium's trail, towers at 10 HP, and only
 * the chapter's three shrooms. LEAVE returns to The Fungi Forest.
 */
export const FUNGI_ARENA: LevelDef = {
  ...MOON_ARENA,
  id: FUNGI_ARENA_LEVEL_ID,
  name: 'The Fungi Arena',
  subtitle: 'The Fungi Forest',
  background: 'bg-fungiarena',
};

export const FUNGI_ARENA_ENEMY_TYPES: EnemyType[] = [
  'redshroom',
  'poisonshroom',
  'skullshroom',
  'purplefungi',
  'glowshroom',
  'blizzardfungi',
  'zombieshroom',
  'hynofungi',
  'themushy',
  'kingshroometron',
];

export const SHADOW_ARENA_ENEMY_TYPES: EnemyType[] = [
  'ghost',
  'thedead',
  'darkghost',
  'darknacho',
  'jackoghost',
  'blueghost',
  'reaper',
];

/** Halolight is sent whole; his halves and quarters come from the split. */
export const CLOUD_ARENA_ENEMY_TYPES: EnemyType[] = [
  'winger',
  'twilight',
  'nightfly',
  'halolight',
  // Heaven's Peak's boss (spec 2026-10-12).
  'lordturtleshell',
];

/** Which Hub arena a HudScene was launched for (`arena: 'moon' | …`). */
export type HubArenaKind = 'moon' | 'shadows' | 'clouds' | 'fungi';

/** The enemies each Hub arena offers — its own roster and nothing else. */
export const HUB_ARENA_ENEMY_TYPES: Readonly<Record<HubArenaKind, EnemyType[]>> = {
  moon: MOON_ARENA_ENEMY_TYPES,
  shadows: SHADOW_ARENA_ENEMY_TYPES,
  clouds: CLOUD_ARENA_ENEMY_TYPES,
  fungi: FUNGI_ARENA_ENEMY_TYPES,
};

/** The Hub arena kind each hub chapter's TEST button launches. */
export function hubArenaKindFor(arenaId: number): HubArenaKind {
  return arenaId === FUNGI_ARENA_LEVEL_ID ? 'fungi' : 'moon';
}

/** The Origin chapter an Origin arena belongs to (its menu is where LEAVE goes). */
export function originArenaChapter(id: number): 'shadows' | 'clouds' | undefined {
  if (id === SHADOW_ARENA_LEVEL_ID) return 'shadows';
  if (id === CLOUD_ARENA_LEVEL_ID) return 'clouds';
  return undefined;
}

/** The Origin arena for a chapter, launched by its menu's TEST button. */
export function originArenaFor(chapter: 'shadows' | 'clouds'): LevelDef {
  return chapter === 'shadows' ? SHADOW_ARENA : CLOUD_ARENA;
}

export function isArenaLevelId(id: number): boolean {
  return (
    id === ARENA_LEVEL_ID ||
    id === MOON_ARENA_LEVEL_ID ||
    id === SHADOW_ARENA_LEVEL_ID ||
    id === CLOUD_ARENA_LEVEL_ID ||
    id === FUNGI_ARENA_LEVEL_ID
  );
}

/** Resolve a level id across the campaign, the hub AND the out-of-band arenas. */
/**
 * The Level Creator's TEST run (spec 2026-11-01): the editor registers the
 * built level here before starting GameScene, so `findLevel(950)` — and so
 * GameScene, HudScene and RETRY — resolve it like any other level.
 */
let creatorLevel: LevelDef | null = null;

export function setCreatorLevel(level: LevelDef | null): void {
  creatorLevel = level;
}

/** An Explore run (spec 2026-11-02): the downloaded level, under id 951. */
let exploreLevel: LevelDef | null = null;

export function setExploreLevel(level: LevelDef | null): void {
  exploreLevel = level;
}

export function findLevel(id: number): LevelDef | undefined {
  if (id === CREATOR_LEVEL_ID) return creatorLevel ?? undefined;
  if (id === EXPLORE_LEVEL_ID) return exploreLevel ?? undefined;
  if (id === ARENA_LEVEL_ID) return TEST_ARENA;
  if (id === MOON_ARENA_LEVEL_ID) return MOON_ARENA;
  if (id === SHADOW_ARENA_LEVEL_ID) return SHADOW_ARENA;
  if (id === CLOUD_ARENA_LEVEL_ID) return CLOUD_ARENA;
  if (id === FUNGI_ARENA_LEVEL_ID) return FUNGI_ARENA;
  return LEVELS.find((l) => l.id === id) ?? findHubLevel(id) ?? findOriginLevel(id);
}

/**
 * Every enemy type the player has actually met — the union of the spawn groups
 * of every unlocked episode. This is the arena's enemy selector list: it grows
 * as the campaign is unlocked rather than spoiling later chapters' roster.
 */
export function unlockedEnemyTypes(isUnlocked: (id: number) => boolean): EnemyType[] {
  const seen: EnemyType[] = [];
  for (const level of LEVELS) {
    if (!isUnlocked(level.id)) continue;
    for (const wave of level.waves) {
      for (const group of wave.groups) {
        if (!seen.includes(group.enemyType)) seen.push(group.enemyType);
      }
    }
  }
  // The Mini Bell never appears in a wave definition — it is only ever
  // summoned from Emperor Pepro. Once the player has met the emperor, his
  // court joins the selector too.
  const emperorAt = seen.indexOf('emperorpepro');
  if (emperorAt >= 0 && !seen.includes('minibell')) {
    seen.splice(emperorAt, 0, 'minibell');
  }
  // The Shrekbruit is ARENA-ONLY and unlocked from the first launch: it appears
  // in no episode, so the campaign sweep above can never surface it, and it is
  // gated on nothing. Appended last so it never displaces the campaign roster's
  // familiar ordering — and so the challenge it guards (kill 100 of them in one
  // wave to earn Shrekbook Hambi) is reachable from a brand-new save.
  seen.push('shrekbruit');
  return seen;
}
